// Financial conflict callout — used on the gate-review page to surface PMRT vs warehouse variance.

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

function toFiniteNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

function formatMoneyUsdM(value) {
  const next = toFiniteNumber(value);
  if (next == null) return '—';
  return `$${next.toFixed(1)}M`;
}

function formatDeltaUsdM(value) {
  const next = toFiniteNumber(value);
  if (next == null) return '—';
  if (Math.abs(next) < 0.05) return '$0.0M';
  return `${next > 0 ? '+' : '-'}$${Math.abs(next).toFixed(1)}M`;
}

function formatPercent(value) {
  const next = toFiniteNumber(value);
  if (next == null) return '—';
  if (Math.abs(next) < 0.05) return '0.0%';
  return `${next > 0 ? '+' : '-'}${Math.abs(next).toFixed(1)}%`;
}

export function FinancialConflictCard({
  id = 'financial-conflict',
  title = 'Financial conflict',
  sourceLabel = 'Approved plan',
  targetLabel = 'Forecast at completion',
  sourceValueUsdM,
  targetValueUsdM,
  varianceUsdM,
  freshness,
  confidence,
  richTooltip = null,
  onDrill,
}) {
  const tooltip = useOptionalRichTooltipController();
  const sourceValue = toFiniteNumber(sourceValueUsdM);
  const targetValue = toFiniteNumber(targetValueUsdM);
  const hasComparison = sourceValue != null && targetValue != null;
  const delta = hasComparison ? targetValue - sourceValue : toFiniteNumber(varianceUsdM);
  const variancePercent =
    hasComparison && sourceValue > 0 && delta != null ? (delta / sourceValue) * 100 : null;
  const isOverPlan = delta != null && delta > 0.05;
  const isUnderPlan = delta != null && delta < -0.05;
  const statusLabel = isOverPlan ? 'Conflict' : isUnderPlan ? 'Favorable' : 'Aligned';
  const statusTone = isOverPlan ? 'red' : isUnderPlan ? 'sage' : 'blue';
  const statusDotClass = isOverPlan ? 'chip-dot pulse' : 'chip-dot';
  const sourceDisplay = formatMoneyUsdM(sourceValue);
  const targetDisplay = formatMoneyUsdM(targetValue);
  const varianceDisplay = formatDeltaUsdM(delta);
  const percentDisplay = formatPercent(variancePercent);
  const targetRatio = hasComparison && sourceValue > 0 ? Math.max(0, targetValue / sourceValue) : 1;
  const scaleRatio = Math.max(1.2, Math.ceil((Math.max(1, targetRatio) + 0.04) * 10) / 10);
  const planPosition = clampPercent((1 / scaleRatio) * 100);
  const forecastPosition = clampPercent((targetRatio / scaleRatio) * 100);
  const withinWidth = isUnderPlan ? forecastPosition : planPosition;
  const varianceLeft = Math.min(planPosition, forecastPosition);
  const varianceWidth = Math.abs(forecastPosition - planPosition);
  const headroomLeft = Math.max(planPosition, forecastPosition);
  const headroomWidth = 100 - headroomLeft;
  const axisHalfPosition = clampPercent((0.5 / scaleRatio) * 100);
  const meterAria = `${sourceLabel} ${sourceDisplay}; ${targetLabel} ${targetDisplay}; variance ${varianceDisplay}${variancePercent != null ? `, ${percentDisplay}` : ''}`;
  const active = Boolean(richTooltip?.surfaceId && tooltip?.isActive(richTooltip.surfaceId));
  const pinned = Boolean(richTooltip?.surfaceId && tooltip?.isPinned(richTooltip.surfaceId));
  const triggerProps =
    richTooltip && tooltip
      ? tooltip.getTriggerProps({
          surfaceId: richTooltip.surfaceId,
          payload: richTooltip,
          testIdPrefix: 'rich-tooltip',
          ariaLabel: `${title} conflict details`,
        })
      : {};
  const TriggerTag = richTooltip ? 'button' : 'div';
  return (
    <article className="conflict-card finance">
      <TriggerTag
        type={richTooltip ? 'button' : undefined}
        className={`conflict-card-trigger${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}
        data-testid={`financial-conflict-trigger-${id}`}
        {...triggerProps}
      >
        <header className="row spread">
          <div>
            <div className="eyebrow">Variance · approved plan × forecast</div>
            <h4 className="card-title">{title}</h4>
          </div>
          <span className={`chip ${statusTone} sm`}>
            <span className={statusDotClass} /> {statusLabel}
          </span>
        </header>
        <div className="conflict-body">
          <div className="conflict-rows">
            <div className="conflict-row">
              <span className="muted small">{sourceLabel}</span>
              <strong className="mono">{sourceDisplay}</strong>
            </div>
            <div className="conflict-row">
              <span className="muted small">{targetLabel}</span>
              <strong className="mono">{targetDisplay}</strong>
            </div>
            <div className="conflict-row delta">
              <span className="muted small">Variance</span>
              <strong className={`mono ${isOverPlan ? 'brick' : isUnderPlan ? 'sage' : 'amber'}`}>
                {varianceDisplay}
              </strong>
              {variancePercent != null ? (
                <span
                  className={`variance-percent-chip ${isOverPlan ? 'brick' : isUnderPlan ? 'sage' : 'amber'}`}
                >
                  {percentDisplay}
                </span>
              ) : null}
            </div>
          </div>
          {hasComparison ? (
            <div
              className={`variance-meter ${isOverPlan ? 'is-over' : isUnderPlan ? 'is-under' : 'is-aligned'}`}
              data-testid={`financial-variance-meter-${id}`}
              role="img"
              aria-label={meterAria}
            >
              <div className="variance-meter-track">
                <span
                  className="variance-meter-segment within"
                  style={{ width: `${withinWidth}%` }}
                />
                {isOverPlan ? (
                  <span
                    className="variance-meter-segment overrun"
                    data-testid={`financial-variance-overrun-${id}`}
                    style={{ left: `${varianceLeft}%`, width: `${varianceWidth}%` }}
                  />
                ) : null}
                {isUnderPlan ? (
                  <span
                    className="variance-meter-segment underrun"
                    data-testid={`financial-variance-underrun-${id}`}
                    style={{ left: `${varianceLeft}%`, width: `${varianceWidth}%` }}
                  />
                ) : null}
                {headroomWidth > 0 ? (
                  <span
                    className="variance-meter-segment headroom"
                    style={{ left: `${headroomLeft}%`, width: `${headroomWidth}%` }}
                  />
                ) : null}
                <span
                  className="variance-meter-marker plan"
                  style={{ left: `${planPosition}%` }}
                  aria-label={`${sourceLabel} marker ${sourceDisplay}`}
                >
                  <span className="variance-meter-marker-label">Plan {sourceDisplay}</span>
                </span>
                <span
                  className="variance-meter-marker forecast"
                  style={{ left: `${forecastPosition}%` }}
                  aria-label={`${targetLabel} marker ${targetDisplay}`}
                />
              </div>
              <div className="variance-meter-axis" aria-hidden="true">
                <span style={{ left: '0%' }}>0%</span>
                <span style={{ left: `${axisHalfPosition}%` }}>50%</span>
                <span style={{ left: `${planPosition}%` }}>100%</span>
                <span style={{ left: '100%' }}>{Math.round(scaleRatio * 100)}%</span>
              </div>
              <div className="variance-meter-legend">
                <span>
                  <i className="legend-swatch within" /> Within plan
                </span>
                <span>
                  <i
                    className={`legend-swatch ${isOverPlan ? 'overrun' : isUnderPlan ? 'underrun' : 'aligned'}`}
                  />{' '}
                  {isOverPlan
                    ? `Overrun ${varianceDisplay} (${percentDisplay})`
                    : isUnderPlan
                      ? `Under plan ${varianceDisplay} (${percentDisplay})`
                      : 'Aligned to plan'}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </TriggerTag>
      <footer className="conflict-footer">
        <span className="muted small">
          Source freshness {freshness || 'mixed'} · confidence {confidence ?? '—'}
        </span>
        <button className="btn link" onClick={onDrill}>
          Open financial detail →
        </button>
      </footer>
    </article>
  );
}
